import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ernaehrungAPI } from '@/lib/api';
import KundeEntwicklung from '@/components/KundeEntwicklung';
import Ernaehrungsprotokoll from '@/components/Ernaehrungsprotokoll';
import { ErnaehrungTresor } from '@/components/ErnaehrungTresor';
import { ErfolgsreportKarte } from '@/components/ErfolgsreportKarte';
import { ErnaehrungAutomatik } from '@/components/ErnaehrungAutomatik';
import { ZieleKarte } from '@/components/ErnaehrungZiele';
import { WettkampfKarte } from '@/components/WettkampfKarte';
import { EinwilligungKarte, ZusatzAboKarte } from '@/components/ErnaehrungAbo';
import { SchritteKarte } from '@/components/ErnaehrungSchritte';
import { CheckinRhythmusKarte } from '@/components/CheckinRhythmusKarte';
import PlanVariantenKarte from '@/components/PlanVarianten';
import { ErnaehrungMitgliederListe, MitgliedFormulare, MitgliedKopf, MitgliedPlan, useMitgliedDaten } from '@/pages/CoachErnaehrung';
import { CockpitUebersicht } from '@/pages/CoachErnaehrungCockpit';
import { Hinweis, KarteSchutz, fehlertext } from '@/pages/CoachErnaehrungTeile';

/* Kunden auf /coach (15.10.2026): Liste → Kundenseite mit denselben Reitern wie Ernährung › Mitglied im CRM —
   Übersicht · Entwicklung · Ernährungsprotokoll — plus „Plan“ und „Formulare“ (die bisherige Mitgliedsansicht der
   Trainer-Webseite, nichts fällt weg). Die Karten der Übersicht sind die CRM-Karten (ZusatzAbo, Einwilligung, Ziele,
   Wettkampf, Tresor, Erfolgsreport, Automatik, Schritte); sie fragen `darf_*`-Felder der Antworten — was die Leitung
   entscheidet, bleibt hier ein Satz. Jede Karte steht für sich (KarteSchutz), ein 403 wird zum Hinweis. */

const REITER = [['uebersicht', 'Übersicht'], ['entwicklung', 'Entwicklung'], ['protokoll', 'Ernährungsprotokoll'], ['plan', 'Plan'], ['formulare', 'Formulare']];
const kuerzel = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((t) => t[0].toUpperCase()).join('');

/* Zusatz-Abo braucht `abo`, `abo_produkt` und den Spiegel `ernaehrung` aus GET /ernaehrung/mitglied/{id} (CRM-Form über den
   Spiegel). Scheitert das (ältere Routen, 403), fehlt nur diese Karte — mit Satz statt Absturz. */
function AboBlock({ leadId }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const laden = useCallback(() => ernaehrungAPI.mitglied(leadId).then((r) => { setD(r.data); setErr(''); }).catch((e) => setErr(fehlertext(e, 'Zusatz-Abo konnte nicht geladen werden'))), [leadId]);
  useEffect(() => { setD(null); setErr(''); laden(); }, [laden]);
  if (err) return <Hinweis>{err}</Hinweis>;
  if (!d) return <Skeleton className="h-32 w-full rounded-[14px]" />;
  return <ZusatzAboKarte leadId={leadId} abo={d.abo} produkt={d.abo_produkt} stand={d.ernaehrung} onNeu={laden} />;
}

/* `eingebettet` (Kundenakte /coach/kunden/:id, Reiter Ernährung): ohne „Alle Kunden“ und Namenszeile — den Kopf zeigt die
   Akte. Reiterwechsel laufen weiter über `gehe({ansicht: 'e-kunden', mitglied, reiter})`; CoachPage leitet das auf die Akte
   (Reiter Ernährung, Unterreiter `ereiter`). */
export function KundenSeite({ coach, ich, mid, reiter: reiterParam, gehe, eingebettet = false }) {
  const { d, err, laden } = useMitgliedDaten(coach, mid);
  const [reiter, setReiter] = useState(reiterParam || 'uebersicht');
  const [protokollTag, setProtokollTag] = useState(null);
  useEffect(() => { if (reiterParam) setReiter(reiterParam); }, [reiterParam]);
  const wechsel = (id) => { setReiter(id); if (id !== 'protokoll') setProtokollTag(null); gehe({ ansicht: 'e-kunden', mitglied: mid, reiter: id }); };
  const zumChat = () => gehe({ ansicht: 'e-cockpit', kunde: mid });
  const zumProtokoll = (t) => { setProtokollTag(t || null); setReiter('protokoll'); };
  const name = d?.lead?.name || '';
  const zurueck = <Button variant="ghost" size="sm" className="-ml-2 gap-1.5 text-muted-foreground" onClick={() => gehe({ ansicht: 'e-kunden' })}><ArrowLeft className="h-4 w-4" /> Alle Kunden</Button>;
  if (err && !d) return <div className="space-y-3">{!eingebettet && zurueck}<Card><CardContent className="py-8 text-center text-sm text-destructive" role="alert">{err}</CardContent></Card></div>;
  return (
    <div className="space-y-4" data-testid="coach-e-kundenseite">
      {!eingebettet && zurueck}
      {!eingebettet && <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-marke/15 text-sm font-extrabold text-marke" aria-hidden="true">{kuerzel(name)}</span>
          <div className="min-w-0">
            {d ? <h2 className="break-words text-2xl font-extrabold tracking-tight">{name || 'Mitglied'}</h2> : <Skeleton className="h-7 w-48" />}
            <MitgliedKopf d={d} />
          </div>
        </div>
        <Button variant="outline" className="gap-1.5 font-bold" onClick={zumChat} data-testid="coach-e-zum-chat"><MessageSquare className="h-4 w-4" /> Chat im Cockpit</Button>
      </div>}
      <div className="flex gap-1 overflow-x-auto rounded-[12px] bg-muted p-1" role="tablist" aria-label="Ansicht">
        {REITER.map(([id, l]) => (
          <button key={id} type="button" role="tab" aria-selected={reiter === id} onClick={() => wechsel(id)} data-testid={`coach-e-reiter-${id}`}
            className={`shrink-0 rounded-[10px] px-3.5 py-2 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${reiter === id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>{l}</button>
        ))}
      </div>

      {reiter === 'entwicklung' && <KarteSchutz titel="Entwicklung" schluessel={mid}><KundeEntwicklung leadId={mid} onProtokoll={zumProtokoll} onChat={zumChat} onSprachnotiz={zumChat} /></KarteSchutz>}
      {reiter === 'protokoll' && <KarteSchutz titel="Ernährungsprotokoll" schluessel={mid}><Ernaehrungsprotokoll leadId={mid} startTag={protokollTag} name={name} /></KarteSchutz>}
      {reiter === 'plan' && <MitgliedPlan coach={coach} mid={mid} d={d} laden={laden} />}
      {/* W3.2: Ernährungsplan mit Varianten (Planart, Steuerung, Wochenplan, Slots aus den Kochbüchern) — dieselbe Karte wie im CRM */}
      {reiter === 'plan' && <KarteSchutz titel="Plan mit Varianten" schluessel={mid}><PlanVariantenKarte leadId={mid} /></KarteSchutz>}
      {reiter === 'formulare' && <MitgliedFormulare coach={coach} mid={mid} d={d} laden={laden} />}
      {reiter === 'uebersicht' && (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="min-w-0 space-y-4">
            <KarteSchutz titel="Übersicht" schluessel={mid}><CockpitUebersicht leadId={mid} onProtokoll={zumProtokoll} onEntwicklung={() => wechsel('entwicklung')} onPlan={() => wechsel('plan')} /></KarteSchutz>
            {/* E1: Tagesprofile je Wochentag, Ausnahmen, Zugangsstufe — rechnet der Server; Felder sind gesperrt, wenn `darf_*` fehlt */}
            <KarteSchutz titel="Ziele" schluessel={mid}><ZieleKarte leadId={mid} /></KarteSchutz>
            {/* E18: Wettkampf-Modus — anlegen nur Leitung (`darf_anlegen`), hier Soll/Ist und Phasen */}
            <KarteSchutz titel="Wettkampf" schluessel={mid}><WettkampfKarte leadId={mid} onGeaendert={laden} /></KarteSchutz>
            {/* E9: Schritte der letzten 28 Tage gegen das Ziel aus E1 */}
            <KarteSchutz titel="Schritte" schluessel={mid}><SchritteKarte leadId={mid} neuLadenBei={d?.heute || ''} /></KarteSchutz>
          </div>
          <div className="space-y-4">
            {/* E0: Zusatz-Abo — anlegen, pausieren, kündigen nur Leitung (`darf_aendern` aus dem Abo-Produkt) */}
            <KarteSchutz titel="Zusatz-Abo" schluessel={mid}><AboBlock leadId={mid} /></KarteSchutz>
            {/* E19: Einwilligung Gesundheitsdaten — Log, Eintragen/Widerruf nur Leitung */}
            <KarteSchutz titel="Einwilligung" schluessel={mid}><EinwilligungKarte leadId={mid} /></KarteSchutz>
            {/* W2.5: Check-in-Rhythmus, Urlaub, Einschieben, Startwerte, Fragebogen (über den Spiegel) */}
            <KarteSchutz titel="Check-in-Rhythmus" schluessel={mid}><CheckinRhythmusKarte leadId={mid} /></KarteSchutz>
            {/* E8: Tresor nur für dieses Mitglied; „für alle“ liegt unter Einstellungen */}
            <KarteSchutz titel="Tresor" schluessel={mid}><ErnaehrungTresor leadId={mid} /></KarteSchutz>
            {/* E15: 3-Monats-Erfolgsreport */}
            <KarteSchutz titel="Erfolgsreport" schluessel={mid}><ErfolgsreportKarte leadId={mid} /></KarteSchutz>
            {/* E7: laufende Sequenzen, nächster Schritt, starten/stoppen */}
            <KarteSchutz titel="Automatik" schluessel={mid}><ErnaehrungAutomatik leadId={mid} /></KarteSchutz>
            {!ich?.master && <Hinweis>Kundenprofil, Check-Up und Körperanalyse-Messungen pflegt das Studio im CRM. Was die Leitung entscheidet (Abo, Einwilligungstext, Wettkampf anlegen), steht hier nur als Hinweis.</Hinweis>}
          </div>
        </div>
      )}
    </div>
  );
}

export function CoachErnaehrungKunden({ coach, ich, mitglied, reiter, gehe, q }) {
  if (mitglied) return <KundenSeite key={mitglied} coach={coach} ich={ich} mid={mitglied} reiter={reiter} gehe={gehe} />;
  return <ErnaehrungMitgliederListe coach={coach} q={q} onWahl={(id) => gehe({ ansicht: 'e-kunden', mitglied: id, reiter: 'uebersicht' })} />;
}

export default CoachErnaehrungKunden;
